import { apiService } from '../api';
import { imageService } from '../imageService';
import type { ApiResponse } from '@/types/apiResponse';
import type {
  BlockedUsersPage,
  ChatAttachmentContentType,
  ChatAttachmentUpload,
  ChatConversationDetail,
  ChatConversationSettings,
  ChatHistoryPage,
  ChatHistoryQuery,
  ChatMembersPage,
  ChatMessageView,
  ChatPerson,
  ChatReportBody,
  ChatScopeKind,
  ChatShareBody,
  ChatShareResult,
  ChatSyncResult,
  ChatUnreadTotal,
  ChatWsTicket,
  DirectConversationResult,
  InboxFilter,
  InboxPage,
  SendChatMessageBody,
} from '@/types/chat';

const BASE = '/private/api/v1/chat';

const conversationPath = (conversationId: string) =>
  `${BASE}/conversations/${encodeURIComponent(conversationId)}`;

const groupPath = (conversationId: string) =>
  `${BASE}/groups/${encodeURIComponent(conversationId)}`;

function toQuery(params: Record<string, string | number | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/** Unified chat REST API. Every method returns the envelope's `result`. */
export const chatService = {
  /** Single-use, 60-second ticket for opening the chat socket. */
  createWsTicket: async (): Promise<ChatWsTicket> => {
    const response = await apiService.post<ApiResponse<ChatWsTicket>>(
      `${BASE}/ws-ticket`,
    );
    return response.result;
  },

  getInbox: async (
    params: { filter?: InboxFilter; cursor?: string; limit?: number } = {},
  ): Promise<InboxPage> => {
    const response = await apiService.get<ApiResponse<InboxPage>>(
      `${BASE}/conversations${toQuery(params)}`,
    );
    return response.result;
  },

  /** Unread messages across all of the caller's chats, for the navbar badge. */
  getUnreadTotal: async (): Promise<ChatUnreadTotal> => {
    const response = await apiService.get<ApiResponse<ChatUnreadTotal>>(
      `${BASE}/unread`,
    );
    return response.result;
  },

  /** Conversations that changed since `since` (catch-up after a reconnect). */
  getSync: async (since: string): Promise<ChatSyncResult> => {
    const response = await apiService.get<ApiResponse<ChatSyncResult>>(
      `${BASE}/sync${toQuery({ since })}`,
    );
    return response.result;
  },

  /**
   * The conversation id of a tournament, match, clan or team chat; the server creates it on first
   * open. 403 `CHAT_SCOPE_FORBIDDEN` when the user isn't in it.
   */
  getScopedConversationId: async (
    kind: ChatScopeKind,
    scopeId: string,
  ): Promise<string> => {
    const response = await apiService.get<
      ApiResponse<{ conversationId: string }>
    >(
      `${BASE}/scoped/${encodeURIComponent(kind)}/${encodeURIComponent(scopeId)}`,
    );
    return response.result.conversationId;
  },

  getConversation: async (
    conversationId: string,
  ): Promise<ChatConversationDetail> => {
    const response = await apiService.get<ApiResponse<ChatConversationDetail>>(
      conversationPath(conversationId),
    );
    return response.result;
  },

  getMessages: async (
    conversationId: string,
    query: ChatHistoryQuery = {},
  ): Promise<ChatHistoryPage> => {
    const response = await apiService.get<ApiResponse<ChatHistoryPage>>(
      `${conversationPath(conversationId)}/messages${toQuery({ ...query })}`,
    );
    return response.result;
  },

  /** 201 = new message, 200 = a retried `clientMsgId` (the original comes back). Same shape either way. */
  sendMessage: async (
    conversationId: string,
    body: SendChatMessageBody,
  ): Promise<ChatMessageView> => {
    const response = await apiService.post<ApiResponse<ChatMessageView>>(
      `${conversationPath(conversationId)}/messages`,
      body,
    );
    return response.result;
  },

  createAttachmentUpload: async (
    conversationId: string,
    contentType: ChatAttachmentContentType,
    size?: number,
  ): Promise<ChatAttachmentUpload> => {
    // With `size` the slot accepts exactly that many bytes.
    const response = await apiService.post<ApiResponse<ChatAttachmentUpload>>(
      `${conversationPath(conversationId)}/attachments`,
      size ? { contentType, size } : { contentType },
    );
    return response.result;
  },

  /** PUT the file to the presigned slot (no auth header — S3 rejects a second auth mechanism). */
  uploadAttachment: (uploadUrl: string, file: File): Promise<void> =>
    imageService.uploadToS3(uploadUrl, file),

  markRead: async (
    conversationId: string,
    seq: number,
  ): Promise<{ lastReadSeq: number }> => {
    const response = await apiService.post<
      ApiResponse<{ lastReadSeq: number }>
    >(`${conversationPath(conversationId)}/read`, { seq });
    return response.result;
  },

  /** `null` unmutes; `CHAT_MUTED_FOREVER` mutes until turned back on. */
  setMute: async (
    conversationId: string,
    mutedUntil: string | null,
  ): Promise<{ mutedUntil: string | null }> => {
    const response = await apiService.put<
      ApiResponse<{ mutedUntil: string | null }>
    >(`${conversationPath(conversationId)}/mute`, { mutedUntil });
    return response.result;
  },

  /**
   * Delete a chat for the caller only: it leaves their inbox and its history up to now is hidden
   * from them. Other members are unaffected, and a new message brings the chat back. 403
   * `CHAT_NOT_MEMBER` when the caller isn't in it.
   */
  deleteConversation: async (
    conversationId: string,
  ): Promise<{ deleted: boolean }> => {
    const response = await apiService.delete<ApiResponse<{ deleted: boolean }>>(
      conversationPath(conversationId),
    );
    return response.result;
  },

  deleteMessage: async (
    messageId: string,
  ): Promise<{ messageId: string; conversationId: string }> => {
    const response = await apiService.delete<
      ApiResponse<{ messageId: string; conversationId: string }>
    >(`${BASE}/messages/${encodeURIComponent(messageId)}`);
    return response.result;
  },

  /** Posting policy and slow mode. Moderators only (OWNER / ADMIN / ORGANISER). */
  updateSettings: async (
    conversationId: string,
    settings: ChatConversationSettings,
  ): Promise<{ settings: Required<ChatConversationSettings> }> => {
    const response = await apiService.put<
      ApiResponse<{ settings: Required<ChatConversationSettings> }>
    >(`${conversationPath(conversationId)}/settings`, settings);
    return response.result;
  },

  /** Owners and organisers first; pending requests are only listed to moderators. */
  getMembers: async (
    conversationId: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<ChatMembersPage> => {
    const response = await apiService.get<ApiResponse<ChatMembersPage>>(
      `${conversationPath(conversationId)}/members${toQuery(params)}`,
    );
    return response.result;
  },

  /** Members who have this chat open right now (the caller included). */
  getOnlineUserIds: async (conversationId: string): Promise<string[]> => {
    const response = await apiService.get<ApiResponse<{ userIds: string[] }>>(
      `${conversationPath(conversationId)}/online`,
    );
    return response.result.userIds;
  },

  /** 403 `CHAT_DIRECT_DISABLED` while direct messages are switched off. */
  openDirect: async (userId: string): Promise<DirectConversationResult> => {
    const response = await apiService.post<
      ApiResponse<DirectConversationResult>
    >(`${BASE}/direct`, { userId });
    return response.result;
  },

  acceptRequest: async (
    conversationId: string,
  ): Promise<{ conversationId: string }> => {
    const response = await apiService.post<
      ApiResponse<{ conversationId: string }>
    >(`${BASE}/requests/${encodeURIComponent(conversationId)}/accept`);
    return response.result;
  },

  /** The sender isn't told. `block` also blocks them. */
  declineRequest: async (
    conversationId: string,
    block = false,
  ): Promise<{ conversationId: string }> => {
    const response = await apiService.post<
      ApiResponse<{ conversationId: string }>
    >(
      `${BASE}/requests/${encodeURIComponent(conversationId)}/decline`,
      block ? { block: true } : {},
    );
    return response.result;
  },

  /** 403 `CHAT_DIRECT_DISABLED` while direct messages are switched off. */
  createGroup: async (
    title: string,
    memberIds: string[],
  ): Promise<{ conversationId: string }> => {
    const response = await apiService.post<
      ApiResponse<{ conversationId: string }>
    >(`${BASE}/groups`, { title, memberIds });
    return response.result;
  },

  renameGroup: async (conversationId: string, title: string): Promise<void> => {
    await apiService.put(groupPath(conversationId), { title });
  },

  /** Blocked or unavailable users are skipped silently. */
  addGroupMembers: async (
    conversationId: string,
    memberIds: string[],
  ): Promise<{ added: number; skipped: number }> => {
    const response = await apiService.post<
      ApiResponse<{ added: number; skipped: number }>
    >(`${groupPath(conversationId)}/members`, { memberIds });
    return response.result;
  },

  removeGroupMember: async (
    conversationId: string,
    userId: string,
  ): Promise<void> => {
    await apiService.delete(
      `${groupPath(conversationId)}/members/${encodeURIComponent(userId)}`,
    );
  },

  /** Group owner only. */
  setGroupAdmin: async (
    conversationId: string,
    userId: string,
    admin: boolean,
  ): Promise<void> => {
    await apiService.put(
      `${groupPath(conversationId)}/admins/${encodeURIComponent(userId)}`,
      { admin },
    );
  },

  /** An owner leaving hands the group on. */
  leaveGroup: async (conversationId: string): Promise<void> => {
    await apiService.post(`${groupPath(conversationId)}/leave`);
  },

  getBlockedUserIds: async (): Promise<string[]> => {
    const response = await apiService.get<ApiResponse<{ userIds: string[] }>>(
      `${BASE}/blocks`,
    );
    return response.result.userIds;
  },

  /**
   * The people the caller blocked, newest block first, with names, cursor-paginated. `q` matches
   * the start of a username or anywhere in a name.
   */
  getBlockedUsers: async (
    params: { q?: string; cursor?: string; limit?: number } = {},
  ): Promise<BlockedUsersPage> => {
    const response = await apiService.get<ApiResponse<BlockedUsersPage>>(
      `${BASE}/blocks/users${toQuery(params)}`,
    );
    return response.result;
  },

  blockUser: async (userId: string): Promise<void> => {
    await apiService.post(`${BASE}/blocks/${encodeURIComponent(userId)}`);
  },

  unblockUser: async (userId: string): Promise<void> => {
    await apiService.delete(`${BASE}/blocks/${encodeURIComponent(userId)}`);
  },

  /** 201 new, 200 when already reported; the caller treats both the same. */
  report: async (body: ChatReportBody): Promise<void> => {
    await apiService.post(`${BASE}/reports`, body);
  },

  /** Username prefix search for the people picker; people blocked either way never appear. */
  searchUsers: async (q: string): Promise<ChatPerson[]> => {
    const response = await apiService.get<ApiResponse<{ users: ChatPerson[] }>>(
      `${BASE}/users${toQuery({ q })}`,
    );
    return response.result.users;
  },

  /** One `clientMsgId` per share. Per-chat results: one chat refusing doesn't stop the rest. */
  share: async (body: ChatShareBody): Promise<ChatShareResult> => {
    const response = await apiService.post<ApiResponse<ChatShareResult>>(
      `${BASE}/share`,
      body,
    );
    return response.result;
  },
};
